import { type FormEvent, useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client.ts";
import { session } from "./session.ts";
import styles from "./SignIn.module.css";

const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

/** Sign-in with a mail code, as in the app; docs/web-designs screen 1. */
export function SignIn() {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [resendIn, setResendIn] = useState(0);
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  async function sendCode(event?: FormEvent) {
    event?.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const devCode = await session.requestCode(email.trim());
      setStep("code");
      setCode(devCode ?? "");
      setResendIn(RESEND_SECONDS);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  async function signIn(event?: FormEvent) {
    event?.preventDefault();
    if (code.length !== CODE_LENGTH) return;
    setBusy(true);
    setError(undefined);
    try {
      await session.verify(email.trim(), code, remember);
    } catch (e) {
      setError(message(e));
      setCode("");
      codeInput.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <aside className={styles.brand}>
        <div className={styles.brandHead}>
          <span className={styles.logo}>V</span>
          <b>Voxie</b>
        </div>
        <h1 className={styles.claim}>Sprechen statt tippen – jetzt auch am Computer.</h1>
        <p className={styles.lead}>Deine Chats, Sprachnachrichten mit Transkript, Themen, Anrufe und Spiele. Gleiches Konto wie in der App.</p>
        <p className={styles.small}>Fürs Handy gibt es die Android-App.</p>
      </aside>
      <main className={styles.formSide}>
        {step === "email" ? (
          <form className={styles.form} onSubmit={sendCode}>
            <h2 className={styles.title}>Anmelden</h2>
            <p className={styles.note}>Wir schicken dir einen Code an deine E-Mail-Adresse.</p>
            <label className={styles.label} htmlFor="email">
              E-Mail
            </label>
            <input
              id="email"
              className={styles.input}
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error && <p className={styles.error}>{error}</p>}
            <button className={styles.primary} disabled={busy || !email.trim()}>
              Code senden
            </button>
          </form>
        ) : (
          <form className={styles.form} onSubmit={signIn}>
            <h2 className={styles.title}>Code eingeben</h2>
            <p className={styles.note}>
              Wir haben einen Code an <b>{email.trim()}</b> geschickt.{" "}
              <button type="button" className={styles.link} onClick={() => (setStep("email"), setError(undefined))}>
                Ändern
              </button>
            </p>
            <div className={styles.code}>
              <input
                ref={codeInput}
                aria-label="Code"
                autoFocus
                className={styles.codeInput}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
              />
              {Array.from({ length: CODE_LENGTH }, (_, i) => (
                <span key={i} className={i === Math.min(code.length, CODE_LENGTH - 1) ? `${styles.digit} ${styles.current}` : styles.digit} aria-hidden>
                  {code[i] ?? ""}
                </span>
              ))}
            </div>
            <label className={styles.trust}>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              <span>
                <b>Diesem Browser vertrauen</b>
                <small>30 Tage angemeldet bleiben, Benachrichtigungen bekommen, Nachrichten für schnelles Laden hier speichern.</small>
              </span>
            </label>
            <p className={styles.hint}>
              Auf einem fremden Computer den Haken weglassen: Dann bleibt nichts gespeichert, und du bist abgemeldet, sobald der Browser schließt.
            </p>
            {error && <p className={styles.error}>{error}</p>}
            <button className={styles.primary} disabled={busy || code.length !== CODE_LENGTH}>
              Anmelden
            </button>
            <p className={styles.resend}>
              Kein Code bekommen?{" "}
              <button type="button" className={styles.link} disabled={resendIn > 0 || busy} onClick={() => sendCode()}>
                {resendIn > 0 ? `Neu senden (${Math.floor(resendIn / 60)}:${String(resendIn % 60).padStart(2, "0")})` : "Neu senden"}
              </button>
            </p>
          </form>
        )}
      </main>
    </div>
  );
}

function message(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "INVALID_CODE") return "Der Code stimmt nicht oder ist abgelaufen.";
    if (e.code === "RATE_LIMITED") return "Zu viele Versuche. Bitte warte etwas und versuch es dann noch einmal.";
    if (e.code === "NETWORK") return "Keine Verbindung zum Server.";
    if (e.status === 400) return "Diese E-Mail-Adresse stimmt so nicht.";
  }
  return "Das hat nicht geklappt. Bitte versuch es noch einmal.";
}
